import { useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Sparkles, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, Spinner } from "@/components/Common";
import { NEW_ITEM } from "@/lib/resume";
import { api, errMsg } from "@/lib/api";

const F = ({ label, children }) => <label className="block space-y-1"><span className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>{children}</label>;
const inp = "bg-[#09090B]";

export const Block = ({ title, action, children, id }) => (
  <Card id={id} className="scroll-mt-24" data-testid={`editor-${id}`}>
    <div className="mb-4 flex items-center justify-between gap-2"><h3 className="font-display text-base text-zinc-100 md:text-lg">{title}</h3>{action}</div>
    {children}
  </Card>
);

const PERSONAL = [["full_name", "Full name"], ["title", "Job title"], ["email", "Email"], ["phone", "Phone"], ["location", "Location"], ["linkedin", "LinkedIn"], ["website", "Website"]];
export const PersonalForm = ({ value, onChange }) => (
  <Block title="Personal info" id="personal">
    <div className="grid gap-3 sm:grid-cols-2">
      {PERSONAL.map(([k, l]) => <F key={k} label={l}><Input value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className={inp} data-testid={`personal-${k}`} /></F>)}
    </div>
  </Block>
);

export const SummaryForm = ({ value, onChange }) => (
  <Block title="Summary" id="summary"><Textarea rows={4} value={value} onChange={(e) => onChange(e.target.value)} placeholder="2–3 lines on who you are and the impact you bring" className={inp} data-testid="summary-input" /></Block>
);

const FIELDS = {
  experience: [["role", "Job title"], ["company", "Company"], ["location", "Location"], ["start", "Start"], ["end", "End"]],
  education: [["degree", "Degree"], ["school", "School"], ["location", "Location"], ["start", "Start"], ["end", "End"]],
  certifications: [["name", "Certification"], ["issuer", "Issuer"], ["date", "Date"]],
};
const TITLES = { experience: "Experience", education: "Education", certifications: "Certifications" };

const Rewrites = ({ options, onPick, onClose, testid }) => (
  <div className="fade-up mt-2 space-y-1.5 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-2" data-testid={`${testid}-options`}>
    <div className="flex items-center justify-between px-1 text-[11px] uppercase tracking-wider text-[#F3C649]">Pick a rewrite<button onClick={onClose} aria-label="Close" className="-m-1.5 rounded-full p-2 hover:bg-white/10" data-testid={`${testid}-options-close`}><X className="h-3.5 w-3.5 text-zinc-400" /></button></div>
    {options.map((o, k) => (
      <button key={k} type="button" onClick={() => onPick(o)} className="block w-full rounded-md border border-[#27272A] bg-[#09090B] p-2 text-left text-sm text-zinc-200 transition-colors hover:border-[#D4AF37]/60" data-testid={`${testid}-option-${k}`}>{o}</button>
    ))}
  </div>
);

const Bullet = ({ b, i, value, onChange, ctx, testid }) => {
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(null);
  const tid = `${testid}-bullet-${i}`;
  const setAt = (v) => onChange(value.map((x, j) => (j === i ? v : x)));
  const improve = async () => {
    if (!b.trim()) return toast.error("Write a bullet first, then improve it");
    setBusy(true);
    try {
      const { data } = await api.post("/resume-builder/rewrite-bullet", { bullet: b, role: ctx.role, company: ctx.company, job_id: ctx.jobId || null });
      const o = (data?.options || []).filter((x) => typeof x === "string" && x.trim()).slice(0, 3);
      o.length ? setOpts(o) : toast.error("No rewrites came back. Try again.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pick = (o) => { const old = b; setAt(o); setOpts(null); toast.success("Bullet improved", { action: { label: "Undo", onClick: () => setAt(old) } }); };
  return (
    <div>
      <div className="flex gap-2">
        <Textarea rows={2} value={b} onChange={(e) => setAt(e.target.value)} placeholder="Start with a verb and a number: Grew revenue 20% by…" className={inp} data-testid={tid} />
        <div className="flex flex-col">
          <Button size="icon" variant="ghost" onClick={improve} disabled={busy} className="text-[#D4AF37] hover:text-[#F3C649]" aria-label="Improve with AI" title="Improve with AI" data-testid={`${tid}-improve`}>{busy ? <Spinner /> : <Sparkles className="h-4 w-4" />}</Button>
          <Button size="icon" variant="ghost" onClick={() => onChange(value.length > 1 ? value.filter((_, j) => j !== i) : [""])} aria-label="Remove bullet" data-testid={`${testid}-bullet-remove-${i}`}><X className="h-4 w-4" /></Button>
        </div>
      </div>
      {opts && <Rewrites options={opts} onPick={pick} onClose={() => setOpts(null)} testid={tid} />}
    </div>
  );
};

const Bullets = ({ value, onChange, ctx, testid }) => (
  <div className="space-y-2 sm:col-span-2">
    <span className="flex items-center justify-between text-[11px] uppercase tracking-wider text-zinc-500">Achievements<span className="flex items-center gap-1 normal-case tracking-normal text-zinc-600"><Sparkles className="h-3 w-3 text-[#D4AF37]" />Improve with AI</span></span>
    {value.map((b, i) => <Bullet key={i} b={b} i={i} value={value} onChange={onChange} ctx={ctx} testid={testid} />)}
    <Button size="sm" variant="ghost" onClick={() => onChange([...value, ""])} className="text-[#F3C649]" data-testid={`${testid}-bullet-add`}><Plus className="h-4 w-4" />Add bullet</Button>
  </div>
);

const ItemCard = ({ kind, item, i, n, set, move, remove, jobId }) => {
  const tid = `${kind}-${i}`;
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#0F0F12] p-4" data-testid={`${kind}-item-${i}`}>
      <div className="mb-3 flex items-center justify-between">
        <span className="truncate text-sm font-medium text-zinc-300">{item[FIELDS[kind][0][0]] || `New ${TITLES[kind].toLowerCase().replace(/s$/, "")}`}</span>
        <div className="flex shrink-0">
          <Button size="icon" variant="ghost" disabled={!i} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`${tid}-up`}><ArrowUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" disabled={i === n - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`${tid}-down`}><ArrowDown className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => remove(i)} className="text-red-400 hover:text-red-300" aria-label="Remove" data-testid={`${tid}-remove`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {FIELDS[kind].map(([k, l]) => (
          <F key={k} label={l}><Input value={item[k]} disabled={k === "end" && item.current} onChange={(e) => set(i, { [k]: e.target.value })} className={inp} data-testid={`${tid}-${k}`} /></F>
        ))}
        {kind === "experience" && <label className="flex items-center gap-2 self-end pb-2 text-sm text-zinc-300"><Switch checked={item.current} onCheckedChange={(v) => set(i, { current: v })} data-testid={`${tid}-current`} />I work here now</label>}
        {kind === "experience" && <Bullets value={item.bullets} onChange={(b) => set(i, { bullets: b })} ctx={{ role: item.role, company: item.company, jobId }} testid={tid} />}
        {kind === "education" && <div className="sm:col-span-2"><F label="Details (optional)"><Input value={item.details} onChange={(e) => set(i, { details: e.target.value })} placeholder="Honors, GPA, coursework" className={inp} data-testid={`${tid}-details`} /></F></div>}
      </div>
    </div>
  );
};

export const ListEditor = ({ kind, items, onChange, jobId }) => {
  const set = (i, p) => onChange(items.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const move = (i, d) => { const a = [...items]; [a[i], a[i + d]] = [a[i + d], a[i]]; onChange(a); };
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <Block title={TITLES[kind]} id={kind} action={<Button size="sm" variant="outline" onClick={() => onChange([...items, NEW_ITEM[kind]()])} data-testid={`${kind}-add`}><Plus className="h-4 w-4" />Add</Button>}>
      <div className="space-y-3">
        {items.map((it, i) => <ItemCard key={it.id} kind={kind} item={it} i={i} n={items.length} set={set} move={move} remove={remove} jobId={jobId} />)}
        {!items.length && <p className="text-sm text-zinc-500">Nothing here yet. Tap Add to create one.</p>}
      </div>
    </Block>
  );
};

export const SkillsEditor = ({ value, onChange }) => {
  const [txt, setTxt] = useState("");
  const add = () => { const n = txt.split(",").map((s) => s.trim()).filter((s) => s && !value.includes(s)); if (n.length) onChange([...value, ...n]); setTxt(""); };
  return (
    <Block title="Skills" id="skills">
      <div className="mb-3 flex flex-wrap gap-2">
        {value.map((s) => <span key={s} className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#F3C649]">{s}<button onClick={() => onChange(value.filter((x) => x !== s))} aria-label={`Remove ${s}`} data-testid={`skill-remove-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><X className="h-3 w-3" /></button></span>)}
      </div>
      <div className="flex gap-2">
        <Input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Type a skill, press Enter (comma-separate many)" className={inp} data-testid="skill-input" />
        <Button variant="outline" onClick={add} data-testid="skill-add">Add</Button>
      </div>
    </Block>
  );
};
